import { MeetingStatus, MeetingType } from 'api/meeting';
import { User } from 'api/user';
export type { ActionItem } from 'api/actionItems';

export type ChatUser = {
  id: string;
  name: string;
  image: string;
  token: string;
};

export enum SortOrder {
  asc = 1,
  desc = -1,
}

export type ChatSwitchCoachParams = {
  coachId: string;
  userIds: string[];
};

export type ScheduleMessageParams = {
  id?: string;
  userId?: string;
  datetime: Date;
  text: string;
  attachments?: string;
};

export interface BaseActivity {
  createdAt: string;
  updatedAt: string;
  type: string;
  userId: string;
}

export interface Activity extends BaseActivity {
  email: string;
  id: number;
  name: string;
  value?: string;
  var_1?: string;
  var_2?: null;
}

export interface CheckIn extends BaseActivity {
  data: string;
}

export type FormInput = {
  id: string;
  type: 'cbt' | 'survey' | 'daily-check-in';
  documentId: string;
  programUid: string;
  programTitle: string;
  userId: string;
  lesson: string;
  lessonTitle: string;
  question: string;
  questionSlug: string;
  answer: string;
  category: string;
  weight: number;
  createdAt: string;
  updatedAt: string;
  var_1: string;
  var_2: string;
  var_3: string;
  var_4: string;
};

export type ActivitySentiment = {
  feeling: string;
  reasons: string[];
  domains: string[];
};

export enum ActionItemType {
  USER_ASSIGNED = 'user.assigned',
  USER_INITIALLY_ASSIGNED = 'user.initially_assigned',
  USER_RENEWED = 'user.renewed',
  USER_CANCELED = 'user.canceled',
  CALL_SCHEDULED = 'call.scheduled',
  CUSTOM = 'custom',
}

export interface IMeeting {
  id: string;
  userId: string;
  email: string;
  name: string;
  status: MeetingStatus;
  /** Null for meetings booked in-house — they have no Calendly event. */
  calendlyUrl: string | null;
  type: MeetingType;
  callUrl?: string;
  startTime: Date;
  endTime: Date;
  timezone: string;
  isShow: boolean;
  hasTranscript?: boolean;
  hasSession?: boolean;
  fathomUrl?: string;
  coachingNote?: {
    id: string;
    meetingId: string;
    userId: string;
    coachId: string;
    note: string;
    createdAt: Date;
    updatedAt: Date;
  } | null;
}

export interface ActionItemData {
  user: User;
}
export interface ActionItemCustomData extends ActionItemData {
  title: string;
  description?: string;
}

export interface ActionItemMeetingData extends ActionItemData {
  meeting: IMeeting;
}

export interface CreateActionItemBody {
  userId: string;
  date?: string;
  title: string;
  description?: string;
}

export interface ChangeActionItemStatusBody {
  userId: string;
  actionItemIds: string[];
  isRead: boolean;
}

export interface DeleteActionItemArgs {
  actionItemId: string;
}

export type TrialPeriodParams = {
  userId: string;
  trialPeriod: number;
};

export enum CbtDataType {
  CBT_PROCRASTINATION = 'cbt-procrastination',
  CBT_INTRO_TO_ADHD = 'cbt-intro-to-adhd',
  CBT_TASK_MANAGEMENT = 'cbt-task-management',
  CBT_TIME_MANAGEMENT = 'cbt-time-management',
  CBT_PROGRESS_DATA = 'cbt-progress-data',
}

export type CbtProgram = {
  lesson: number;
  isDone: boolean;
  programUid: CbtDataType;
  title: string;
  lessons: Lesson[];
};
export type CbtProgressData = {
  currentProgram: CbtDataType;
  programs: Record<CbtDataType, CbtProgram>;
};

export type CbtActivityType = {
  createdAt: string;
  updatedAt: string;
  userId: string;
  type: CbtDataType;
  data: any;
  _id: string;
};

export type LessonStep = Record<string, string[]>;

export type Lesson = {
  title?: string;
  status: 'done' | 'active';
  steps: Record<string, LessonStep>;
  slug: string;
};

export type StepLessons = {
  lessons: Record<string, Lesson>;
};

export type ApiResponse<T = any> = {
  status: 'OK' | 'error';
  data?: T;
};

export type ProgramAnswers = {
  lessons: {
    [key: string]: {
      status: 'active' | 'done' | 'locked';
      steps: {
        [key: string]: {
          answers: string[];
        };
      };
    };
  };
};

export enum ChatMessageCustomType {
  Bot = 'bot',
  Audio = 'audio',
  Video = 'video',
  CbtVideo = 'cbt-video',
  CbtLessonAnswers = 'cbt-lesson-answers',
  SurveyResults = 'survey-results',
  DailyCheckInAnswers = 'daily-check-in-answers',
  MeetingSummary = 'meeting-summary',
}

export type DailyCheckIn = {
  feeling: 'Awful' | 'Bad' | 'Meh' | 'Good' | 'Awesome';
  reasons: string[];
  domains: string[];
};

export enum MessageRole {
  system = 'system',
  assistant = 'assistant',
  user = 'user',
}

export enum ContextType {
  chatHistory = 'chatHistory',
  cbtLessons = 'cbtLessons',
  none = 'none',
}

export type AssistantInteractionMessageCitation = {
  marker: string;
  toolIndex: number;
  tool: string;
  summary: string | null;
  rationale: string | null;
};

export type AssistantInteractionMessage = {
  role: MessageRole;
  content: string;
  id?: number;
  createdAt?: string;
  updatedAt?: string;
  interactionId?: number;
  contextType?: ContextType;
  rating?: number | null;
  citations?: AssistantInteractionMessageCitation[];
};

export type Interaction = {
  userId: string;
  messages: AssistantInteractionMessage[];
  id?: number;
  createdAt?: string;
  updatedAt?: string;
  coachId?: string;
  messageTotal?: number;
};

export declare type EnumValues<T = string> = [T, ...T[]];
